import { LocalDatasheetTabDto } from '../components/editor/types/tab-dto';
import { DatasheetFieldComponentGetDto } from '@common/dto/datasheet/field-component/get-dto';
import { DatasheetTextComponentGetDto } from '@common/dto/datasheet/text-component/get-dto';
import { DatasheetFieldComponentOriginFieldGetDto } from '@common/dto/datasheet/field-component/origin-field-dto';
import type { FormatPayload, TabData, Field, TextItem, FieldMetadata } from '../components/editor/types/api-payload';
import type { DatasheetElementSettingsFieldComponentValue } from '../components/editor/components/datasheet-element-settings/controllers/main';
import type { DatasheetElementSettingsTextComponentValue } from '../components/editor/components/datasheet-element-settings/controllers/main';
import type { DatasheetGridSettingsDto } from '@common/dto/datasheet/grid-settings-dto';
import { DatasheetGridSettingsMarginType } from '@common/dto/datasheet/grid-settings-dto';
import { DatasheetFieldComponentTextAlign } from '@common/dto/datasheet/field-component/text-align';
import { DatasheetFieldComponentHeadingPosition } from '@common/dto/datasheet/field-component/heading-position';
import { DatasheetElement, DatasheetElementType } from '../types/datasheet-element';
import { DatasheetComponentTextStyle } from '@common/dto/datasheet/component-settings/text-style';

type OriginFieldInput = Partial<FieldMetadata> & Partial<DatasheetFieldComponentOriginFieldGetDto>;

type FieldComponentInput = Omit<Partial<Field>, 'field' | 'tableReportFormat' | 'theme'> & Partial<DatasheetFieldComponentGetDto>;

type TextComponentInput = Partial<TextItem> & Partial<DatasheetTextComponentGetDto>;

function convertTextStyleToNumber(textStyle: Array<any> | number | undefined): number {
  if (typeof textStyle === 'number') return textStyle;
  if (!Array.isArray(textStyle)) return 0;
  return textStyle.reduce((acc, style) => acc | (typeof style === 'number' ? style : 0), 0) || 33;
}

function convertFontColorToNumber(fontColor: number | string | undefined): number {
  if (typeof fontColor === 'number') return fontColor;
  if (typeof fontColor === 'string') {
    const hex = fontColor.startsWith('#') ? fontColor.slice(1) : fontColor;
    const parsed = parseInt(hex, 16);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

function convertAlignToNumber(align: any): number {
  if (typeof align === 'number') return align;
  if (align === DatasheetFieldComponentTextAlign.left) return 0;
  if (align === DatasheetFieldComponentTextAlign.center) return 1;
  if (align === DatasheetFieldComponentTextAlign.right) return 2;
  return 0;
}

function convertHeadPosToNumber(headPos: any): number {
  if (typeof headPos === 'number') return headPos;
  if (headPos === DatasheetFieldComponentHeadingPosition.top) return 0;
  if (headPos === DatasheetFieldComponentHeadingPosition.left) return 1;
  if (headPos === DatasheetFieldComponentHeadingPosition.bottom) return 2;
  if (headPos === DatasheetFieldComponentHeadingPosition.right) return 3;
  return 0;
}

function convertAlignToApiNumber(align: any): number {
  if (typeof align === 'number') return align;
  if (align === DatasheetFieldComponentTextAlign.left) return 0;
  if (align === DatasheetFieldComponentTextAlign.center) return 1;
  if (align === DatasheetFieldComponentTextAlign.right) return 2;
  return 0;
}

function convertColorToNumber(color: string): number {
  const hex = color.startsWith('#') ? color.slice(1) : color;
  const parsed = parseInt(hex, 16);
  return isNaN(parsed) ? 0 : parsed;
}

function convertNumberToColor(num: number): string {
  // Extract BGR bytes from number and output as RGB hex
  // Number is stored as BGR: bits 0-7 = Blue, 8-15 = Green, 16-23 = Red
  const blue = (num & 0xFF);
  const green = ((num >> 8) & 0xFF);
  const red = ((num >> 16) & 0xFF);
  // Output as RGB hex string
  const hex = [
    red.toString(16).padStart(2, '0'),
    green.toString(16).padStart(2, '0'),
    blue.toString(16).padStart(2, '0')
  ].join('');
  return `#${hex}`;
}

function convertApiNumberToAlign(num: number): DatasheetFieldComponentTextAlign {
  switch (num) {
    case 0:
      return DatasheetFieldComponentTextAlign.left;
    case 1:
      return DatasheetFieldComponentTextAlign.center;
    case 2:
      return DatasheetFieldComponentTextAlign.right;
    default:
      return DatasheetFieldComponentTextAlign.left;
  }
}

function convertApiNumberToHeadPos(num: number): DatasheetFieldComponentHeadingPosition {
  // 0 = top, 1 = left, 2 = bottom, 3 = right
  switch (num) {
    case 0:
      return DatasheetFieldComponentHeadingPosition.top;
    case 1:
      return DatasheetFieldComponentHeadingPosition.left;
    case 2:
      return DatasheetFieldComponentHeadingPosition.bottom;
    case 3:
    default:
      return DatasheetFieldComponentHeadingPosition.right;
  }
}

function convertNumberToTextStyleArray(num: number): Array<DatasheetComponentTextStyle> {
  if (num === 0 || num === 33 || num === 37) return [];
  
  // Decode number back to array of styles using bitwise AND checks
  // Enum values: bold=4, italic=2, underline=16 (matching Java DomConstants bit masks)
  const styles: Array<DatasheetComponentTextStyle> = [];
  
  // Use bitwise AND to check which flags are set
  if ((num & DatasheetComponentTextStyle.bold) === DatasheetComponentTextStyle.bold) {
    styles.push(DatasheetComponentTextStyle.bold);
  }
  if ((num & DatasheetComponentTextStyle.italic) === DatasheetComponentTextStyle.italic) {
    styles.push(DatasheetComponentTextStyle.italic);
  }
  if ((num & DatasheetComponentTextStyle.underline) === DatasheetComponentTextStyle.underline) {
    styles.push(DatasheetComponentTextStyle.underline);
  }
  
  return styles;
}

function convertFieldToApiFormat(
  fieldComponent: FieldComponentInput,
  tabId: number,
  elementX?: number,
  elementY?: number,
  elementWidth?: number,
  elementHeight?: number
): Field {
  const originField: OriginFieldInput = fieldComponent.field ?? {};
  const x = elementX !== undefined ? elementX : (fieldComponent.x ?? 0);
  const y = elementY !== undefined ? elementY : (fieldComponent.y ?? 0);
  const width = elementWidth !== undefined ? elementWidth : (fieldComponent.width ?? 0);
  const height = elementHeight !== undefined ? elementHeight : (fieldComponent.height ?? 0);

  let context = 0;
  const fieldContextValue = originField.context ?? originField.fieldcontext;

  if (typeof fieldContextValue === 'number') {
    context = fieldContextValue;
  } else if (fieldContextValue === 'IDS_CONTEXT' || fieldContextValue === '1') {
    context = 1;
  } else if (fieldContextValue === 'PDS_CONTEXT' || fieldContextValue === '2') {
    context = 2;
  } else if (fieldContextValue === '8' || fieldContextValue === 'TABLE_CONTEXT') {
    context = 8;
  }

  const fieldId = originField.fieldid ?? originField.id ?? 0;
  const system = originField.fieldtype?.systemType !== undefined
    ? originField.fieldtype.systemType
    : (originField.system !== undefined
      ? originField.system
      : (originField.systemField !== undefined ? originField.systemField : false));
  const fieldname = originField.fieldname ?? originField.fieldnameWithPath ?? '';

  let textStyleNum = convertTextStyleToNumber(fieldComponent.textstyle ?? fieldComponent.textStyle);
  if (fieldComponent.headdisplay !== undefined && fieldComponent.headdisplay !== null) {
    textStyleNum = fieldComponent.headdisplay ? (textStyleNum | 1) : (textStyleNum & ~1);
  }
  if (fieldComponent.showborder !== undefined && fieldComponent.showborder !== null) {
    textStyleNum = fieldComponent.showborder ? (textStyleNum | 32) : (textStyleNum & ~32);
  }

  return {
    id: fieldComponent.id || fieldId,
    tabId,
    x,
    y,
    width,
    height,
    tabOrder: fieldComponent.tabOrder ?? 0,
    contentAlign: convertAlignToApiNumber(fieldComponent.contentalign ?? fieldComponent.contentAlign),
    fontColor: convertFontColorToNumber(fieldComponent.fontcolor ?? fieldComponent.fontColor),
    fontName: fieldComponent.fontname || fieldComponent.fontName || 'Arial',
    fontSize: fieldComponent.fontsize || fieldComponent.fontSize || 10,
    headAlign: convertAlignToApiNumber(fieldComponent.headalign ?? fieldComponent.headAlign),
    headColor: convertFontColorToNumber(fieldComponent.headcolor ?? fieldComponent.headColor),
    headFont: fieldComponent.headfont || fieldComponent.headFont || 'Tahoma',
    headingPds: (fieldComponent.headtext && fieldComponent.headtext.trim() !== ''
      ? fieldComponent.headtext
      : (fieldComponent.headingPds ?? fieldComponent.headingpds ?? '')) || '',
    headPos: convertHeadPosToNumber(fieldComponent.headpos ?? fieldComponent.headPos),
    headFontSize: fieldComponent.headfontsize || fieldComponent.headFontSize || 8,
    headStyle: convertTextStyleToNumber(fieldComponent.headstyle ?? fieldComponent.headStyle),
    helpText: fieldComponent.helptext || fieldComponent.helpText || null,
    showFieldType: fieldComponent.showfieldtype ?? fieldComponent.showFieldType ?? true,
    textStyle: textStyleNum,
    required: fieldComponent.required ?? false,
    confirmEntry: fieldComponent.confirmentry ?? fieldComponent.confirmEntry ?? false,
    dropdownEdit: fieldComponent.dropdownedit ?? fieldComponent.dropdownEdit ?? false,
    headerRenderWidth: fieldComponent.headerRenderWidth || 0,
    tabs: fieldComponent.tabs || [],
    field: {
      fieldid: fieldId,
      system,
      context,
      fieldname,
    },
    subfields: (fieldComponent.dssubfields || fieldComponent.m_ds_subfields || fieldComponent.subfields)
      ?.map((sub) => convertFieldToApiFormat(sub as FieldComponentInput, tabId)) || [],
    tableReportFormat: typeof fieldComponent.tableReportFormat === 'string'
      ? fieldComponent.tableReportFormat
      : null,
  };
}

function convertTextToApiFormat(
  formatId: number,
  textComponent: TextComponentInput,
  tabId: number,
  elementX?: number,
  elementY?: number,
  elementWidth?: number,
  elementHeight?: number
): TextItem {
  const x = elementX !== undefined ? elementX : (textComponent.x ?? 0);
  const y = elementY !== undefined ? elementY : (textComponent.y ?? 0);
  const width = elementWidth !== undefined ? elementWidth : (textComponent.width ?? 0);
  const height = elementHeight !== undefined ? elementHeight : (textComponent.height ?? 0);

  return {
    id: formatId,
    tabId,
    x,
    y,
    width,
    height,
    textStyle: convertTextStyleToNumber(textComponent.textstyle ?? textComponent.textStyle),
    fontColor: convertFontColorToNumber(textComponent.fontColor),
    fontSize: textComponent.fontsize || textComponent.fontSize || 14,
    fontName: textComponent.fontname || textComponent.fontName || 'Arial',
    text: textComponent.text || '',
    hexColor: textComponent.hexColor || '#000000',
    idType: textComponent.idType || 3,
    idKey: textComponent.idKey ?? -1,
    textAlign: textComponent.align !== undefined
      ? convertAlignToApiNumber(textComponent.align)
      : (textComponent.textAlign ?? 0),
  };
}

/**
 * Converts storage elements to API format fields and texts
 */
function convertStorageElementsToApiFormat(
  elements: Record<string, DatasheetElement>,
  tabId: number,
  formatId: number,
  filterTabId?: number
): { fields: Field[]; texts: TextItem[] } {
  const fields: Field[] = [];
  const texts: TextItem[] = [];
  const textOwnerTabId = filterTabId !== undefined ? filterTabId : tabId;

  Object.values(elements).forEach((element) => {
    if (element.type === DatasheetElementType.field) {
      const fieldComponent = element.original as DatasheetFieldComponentGetDto;
      fields.push(convertFieldToApiFormat(
        fieldComponent,
        tabId,
        element.coordinates.x,
        element.coordinates.y,
        element.width,
        element.height
      ));
    } else if (element.type === DatasheetElementType.text) {
      const textComponent = element.original as DatasheetTextComponentGetDto;
      const elementTabId = textComponent.tabId;
      if (elementTabId !== undefined && elementTabId != textOwnerTabId) {
        return; // skip — this text belongs to another tab
      }
      texts.push(convertTextToApiFormat(formatId,
        textComponent,
        tabId,
        element.coordinates.x,
        element.coordinates.y,
        element.width,
        element.height
      ));
    }
  });
  
  // Sort fields by tabOrder
  fields.sort((a, b) => (a.tabOrder || 0) - (b.tabOrder || 0));
  
  return { fields, texts };
}

export function convertDatasheetTabsToApiPayload(
  tabs: LocalDatasheetTabDto[],
  formatId: number,
  oldFormatId: number = formatId,
  getTabElements?: (tabId: string | number) => Record<string, DatasheetElement> | undefined,
  notLoaded?: { tabsData?: Array<{ id: number; fields?: FieldComponentInput[] }> } | null,
  isSaveAs?: boolean
): FormatPayload {
  const tabsData: TabData[] = tabs.map((tab, index) => {
    const tabId = typeof tab.id === 'string' ? -1 : (tab.id || -1);
    const oldTabId = typeof tab.oldTabId === 'string' ? parseInt(tab.oldTabId) : (tab.oldTabId ?? 0);
    const resolvedTabId = (isSaveAs || tabId === -1) ? -(index + 1) : tabId;

    let fields: Field[] = [];
    let texts: TextItem[] = [];

    if (getTabElements) {
      const elements = getTabElements(tab.id);
      if (elements && Object.keys(elements).length > 0) {
        const converted = convertStorageElementsToApiFormat(elements, resolvedTabId, formatId, tabId > 0 ? tabId : undefined);
        fields = converted.fields;
        texts = converted.texts;
        let tabFields: FieldComponentInput[] = tab.fields || [];
        if (tabFields.length === 0 && notLoaded?.tabsData) {
          const apiTab =
            notLoaded.tabsData.find((t) => String(t.id) === String(tab.id)) ??
            notLoaded.tabsData[index];
          tabFields = apiTab?.fields || [];
        }
        if (tabFields.length > 0) {
          fields = fields.map((field) => {
            const matchingTabField = tabFields.find((tf) => {
              const tabFieldId = tf.id ?? tf.field?.fieldid;
              const fieldId = field.id ?? field.field?.fieldid;
              return String(tabFieldId) === String(fieldId);
            });

            if (!matchingTabField) {
              return field;
            }

            const headingPds =
              typeof matchingTabField.headingPds === 'string' &&
              matchingTabField.headingPds.trim() !== ''
                ? matchingTabField.headingPds
                : (matchingTabField.field?.fieldname &&
                   String(matchingTabField.field.fieldname).trim() !== '')
                ? matchingTabField.field.fieldname
                : field.headingPds;
            const headStyle =
              matchingTabField.headStyle != null && matchingTabField.headStyle !== 0
                ? matchingTabField.headStyle
                : field.headStyle;
            const textStyle =
              matchingTabField.textStyle != null && matchingTabField.textStyle !== 0
                ? matchingTabField.textStyle
                : field.textStyle;

            return {
              ...field,
              headingPds,
              headStyle,
              textStyle,
              field: {
                ...field.field,
                fieldname: matchingTabField.field?.fieldname || field.field.fieldname,
              },
            };
          });
        }
      } else {
        fields = tab.fields?.map(field => convertFieldToApiFormat(field, resolvedTabId)) || [];
        texts = tab.texts?.map(text => convertTextToApiFormat(formatId, text, resolvedTabId)) || [];
      }
    } else {
      fields = tab.fields?.map(field => convertFieldToApiFormat(field, resolvedTabId)) || [];
      texts = tab.texts?.map(text => convertTextToApiFormat(formatId, text, resolvedTabId)) || [];
    }

    return {
      id: resolvedTabId,
      oldTabId: oldTabId || resolvedTabId,
      index: tab.index ?? index,
      name: tab.name,
      fields,
      texts,
    };
  });

  return {
    id: formatId,
    oldFormatId,
    tabsData,
  };
}

export function convertUIFormatsToApiDefaults(
  defaultFieldComponentSettings: DatasheetElementSettingsFieldComponentValue,
  defaultTextComponentSettings: DatasheetElementSettingsTextComponentValue,
  gridSettings: DatasheetGridSettingsDto,
  formatId: number = 0,
  context: number = 0
): any {
  const content = defaultFieldComponentSettings.content;
  const heading = defaultFieldComponentSettings.heading;
  
  return {
    id: formatId,
    context: context,
    showBorder: content.showBorder,
    showFieldType: content.showFieldType,
    showHeading: heading.displayHeading,
    dataColor: convertColorToNumber(content.color),
    dataSize: content.fontSize,
    dataFont: content.fontFamily,
    dataStyle: (() => {
      let styleNum = convertTextStyleToNumber(content.styles);
      if (content.showBorder) {
        styleNum = styleNum | 32;
      } else {
        styleNum = styleNum & ~32;
      }
      return styleNum;
    })(),
    dataAlign: convertAlignToApiNumber(content.align),
    headColor: convertColorToNumber(heading.color),
    headSize: heading.fontSize,
    headFont: heading.fontFamily,
    headStyle: convertTextStyleToNumber(heading.textStyles),
    headAlign: convertAlignToApiNumber(heading.align),
    headPos: convertHeadPosToNumber(heading.position),
    textColor: convertColorToNumber(defaultTextComponentSettings.color),
    textSize: defaultTextComponentSettings.fontSize,
    textFont: defaultTextComponentSettings.fontFamily,
    textStyle: convertTextStyleToNumber(defaultTextComponentSettings.textStyle),
    gridX: gridSettings.horizontalGrid,
    gridY: gridSettings.verticalGrid,
    showGrid: gridSettings.showGrid,
    snapGrid: gridSettings.snapToGrid,
    showPageBreaks: gridSettings.showPageBreaks,
    showMargins: gridSettings.showMargins,
    showMarginsStyle: gridSettings.marginType === 'fhq' ? 0 : gridSettings.marginType === 'tablet' ? 1 : 2,
    showMarginsOtherX: gridSettings.marginWidth,
    showMarginsOtherY: gridSettings.marginHeight,
  };
}

export function convertApiDefaultsToUIFormats(apiDefaults: any): {
  defaultFieldComponentSettings: DatasheetElementSettingsFieldComponentValue;
  defaultTextComponentSettings: DatasheetElementSettingsTextComponentValue;
  gridSettings: DatasheetGridSettingsDto;
} {
  return {
    defaultFieldComponentSettings: {
      content: {
        fontFamily: apiDefaults.dataFont || 'Arial',
        fontSize: apiDefaults.dataSize || 10,
        color: convertNumberToColor(apiDefaults.dataColor || 0),
        showBorder: (() => {
          if (apiDefaults.showBorder !== undefined && apiDefaults.showBorder !== null) {
            return apiDefaults.showBorder;
          }
          const dataStyleNum = typeof apiDefaults.dataStyle === 'number' ? apiDefaults.dataStyle : 0;
          return (dataStyleNum & 32) === 32;
        })(),
        showFieldType: apiDefaults.showFieldType ?? true,
        styles: convertNumberToTextStyleArray(apiDefaults.dataStyle || 33),
        align: convertApiNumberToAlign(apiDefaults.dataAlign || 0),
        tabOrder: 1,
      },
      heading: {
        displayHeading: apiDefaults.showHeading ?? true,
        textValue: '',
        fontFamily: apiDefaults.headFont || 'Tahoma',
        fontSize: apiDefaults.headSize || 8,
        color: convertNumberToColor(apiDefaults.headColor || 0),
        position: convertApiNumberToHeadPos(apiDefaults.headPos ?? 1),
        align: convertApiNumberToAlign(apiDefaults.headAlign || 0),
        textStyles: convertNumberToTextStyleArray(apiDefaults.headStyle || 33),
      },
      helpText: {
        textValue: '',
      },
      table: undefined,
    },
    defaultTextComponentSettings: {
      fontFamily: apiDefaults.textFont || 'Arial',
      fontSize: apiDefaults.textSize || 14,
      color: convertNumberToColor(apiDefaults.textColor || 0),
      textStyle: convertNumberToTextStyleArray(apiDefaults.textStyle || 37),
      text: '',
      align: convertApiNumberToAlign(apiDefaults.align || 0),
    },
    gridSettings: {
      horizontalGrid: apiDefaults.gridX || 30,
      verticalGrid: apiDefaults.gridY || 30,
      showGrid: apiDefaults.showGrid ?? true,
      showPageBreaks: apiDefaults.showPageBreaks ?? false,
      snapToGrid: apiDefaults.snapGrid ?? true,
      showEditTabOrder: false,
      showMargins: apiDefaults.showMargins ?? false,
      marginType: apiDefaults.showMarginsStyle === 0 ? DatasheetGridSettingsMarginType.fhq : apiDefaults.showMarginsStyle === 1 ? DatasheetGridSettingsMarginType.tablet : DatasheetGridSettingsMarginType.custom,
      marginWidth: apiDefaults.showMarginsOtherX || 400,
      marginHeight: apiDefaults.showMarginsOtherY || 300,
    },
  };
}

